import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Mail, Sparkles } from "lucide-react";
import { lookupApplication } from "@/lib/apiClient";
import MoodBoardForm from "@/components/MoodBoardForm";
import useDocumentSeo from "@/lib/useDocumentSeo";

const PROD_URL = "https://model-workspace-1.emergent.host";
const CANONICAL = `${PROD_URL}/free-shoot`;

export default function FreeShoot() {
  const [email, setEmail] = useState("");
  const [checking, setChecking] = useState(false);
  const [lookup, setLookup] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useDocumentSeo({
    title: "Free Editorial Shoot for Models | VOGUEVERVE",
    description: "Signed up on VOGUEVERVE? Submit your mood board and preferred dates for a chance at a free editorial shoot. Approved mood boards get a confirmed shoot email.",
    canonical: CANONICAL,
    ogImage: `${PROD_URL}/og-image.jpg`,
    keywords: "free editorial shoot, model shoot, portfolio building, free photoshoot for models, VOGUEVERVE free shoot",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "url": CANONICAL,
      "name": "Free Editorial Shoot for Models | VOGUEVERVE",
      "description": "Existing VOGUEVERVE applicants can submit a mood board and preferred dates for a free editorial shoot.",
      "isPartOf": { "@id": `${PROD_URL}/#website` },
    },
  });

  const check = async (e) => {
    e?.preventDefault?.();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email.");
      return;
    }
    setChecking(true);
    setNotFound(false);
    try {
      const result = await lookupApplication(email.trim());
      if (result.found) {
        setLookup(result);
      } else {
        setNotFound(true);
      }
    } catch {
      toast.error("Could not verify your account. Try again.");
    } finally {
      setChecking(false);
    }
  };

  const reset = () => {
    setLookup(null);
    setNotFound(false);
    setEmail("");
  };

  return (
    <main data-testid="free-shoot-page" className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Link to="/" data-testid="fs-back-home" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-white/60 hover:text-white transition-colors">
            <ArrowLeft size={14} /> Back
          </Link>
          <span className="font-display text-lg md:text-xl font-black tracking-tighter uppercase">
            Vogue<span className="text-white/70">Verve</span>
          </span>
          <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 hidden sm:inline">
            Free Shoot Program
          </span>
        </div>
      </header>

      {!lookup && !notFound && (
        <section className="flex-1 flex flex-col justify-center px-6 md:px-10 py-16">
          <div className="max-w-3xl mx-auto w-full">
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] font-bold text-white/50 mb-6"
            >
              <Sparkles size={12} /> An invite only for VOGUEVERVE applicants
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-script text-white text-6xl sm:text-7xl md:text-8xl leading-[0.9] mb-6"
            >
              a free shoot,
              <br />
              on us.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="max-w-xl text-lg text-white/60 leading-relaxed mb-10"
            >
              Signed up on VOGUEVERVE already? Drop your mood board and preferred dates. If our creative team approves it, we&rsquo;ll email you a confirmed shoot slot — no cost, portfolio yours to keep.
            </motion.p>

            <motion.form
              onSubmit={check}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45 }}
              className="space-y-4"
              data-testid="fs-email-form"
            >
              <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/50 flex items-center gap-2">
                <Mail size={12} /> The email you applied with
              </label>
              <input
                data-testid="fs-email-input"
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="w-full bg-transparent border-0 border-b border-white/25 text-2xl md:text-3xl font-body text-white placeholder:text-white/25 focus:outline-none focus:border-white pb-3"
              />
              <button
                data-testid="fs-check-btn"
                type="submit"
                disabled={checking}
                className="inline-flex items-center gap-3 rounded-full bg-white text-black px-8 py-4 text-xs uppercase tracking-[0.25em] font-bold active:scale-[0.98] transition-transform disabled:opacity-60"
              >
                {checking ? (
                  <><Loader2 size={16} className="animate-spin" /> Checking…</>
                ) : (
                  <>Continue <ArrowRight size={14} /></>
                )}
              </button>
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/30 pt-2">
                Only applicants already registered on VOGUEVERVE are eligible.
              </p>
            </motion.form>
          </div>
        </section>
      )}

      {notFound && (
        <section className="flex-1 flex flex-col justify-center px-6 md:px-10 py-16">
          <div className="max-w-2xl mx-auto w-full" data-testid="fs-not-found">
            <p className="text-[10px] uppercase tracking-[0.35em] font-bold text-white/50 mb-6">
              We don&rsquo;t have you on file yet
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight text-white leading-tight mb-5">
              First things first — apply.
            </h2>
            <p className="text-base text-white/60 leading-relaxed mb-8 max-w-lg">
              The free shoot program is reserved for applicants who have completed the Get Scouted profile. Take 3 minutes to apply, then come back.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/get-scouted"
                data-testid="fs-goto-apply"
                className="inline-flex items-center gap-3 rounded-full bg-white text-black px-8 py-4 text-xs uppercase tracking-[0.25em] font-bold active:scale-[0.98] transition-transform"
              >
                Start my application <ArrowRight size={14} />
              </Link>
              <button
                data-testid="fs-try-again"
                onClick={reset}
                className="inline-flex items-center gap-3 rounded-full border border-white/25 text-white px-8 py-4 text-xs uppercase tracking-[0.25em] font-bold active:scale-[0.98] transition-transform"
              >
                Try another email
              </button>
            </div>
          </div>
        </section>
      )}

      {lookup && (
        <section className="flex-1 px-6 md:px-10 py-12">
          <div className="max-w-2xl mx-auto w-full">
            <p className="text-[10px] uppercase tracking-[0.35em] font-bold text-white/50 mb-4">
              Verified · {lookup.status}
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight text-white leading-tight mb-3">
              Welcome back, {(lookup.full_name || "").split(" ")[0] || "darling"}.
            </h2>
            <p className="text-base text-white/60 leading-relaxed mb-10 max-w-lg">
              Drop the shoot you&rsquo;d love. Our creative team reviews every mood board. If approved, we&rsquo;ll email you with dates and a call sheet.
              {lookup.mood_boards_count > 0 && (
                <> You&rsquo;ve already submitted <strong className="text-white">{lookup.mood_boards_count}</strong> mood {lookup.mood_boards_count === 1 ? "board" : "boards"} — you can add another below.</>
              )}
            </p>

            <MoodBoardForm
              applicationId={lookup.id}
              email={email}
              applicantName={lookup.full_name || ""}
            />

            <button
              data-testid="fs-switch-account"
              onClick={reset}
              className="mt-10 text-[10px] uppercase tracking-[0.3em] font-bold text-white/40 hover:text-white transition-colors"
            >
              Not you? Switch account
            </button>
          </div>
        </section>
      )}

      <footer className="border-t border-white/10 py-6">
        <div className="max-w-4xl mx-auto px-6 md:px-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/30">
            &copy; {new Date().getFullYear()} VOGUEVERVE Model Management
          </p>
          <div className="flex gap-6">
            <Link to="/legal#terms" className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/50 hover:text-white transition-colors">Terms</Link>
            <Link to="/legal#privacy" className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/50 hover:text-white transition-colors">Privacy</Link>
            <a href="tel:+910000000000" className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/50 hover:text-white transition-colors">Support</a>
          </div>
        </div>
      </footer>
    </main>
  );
}
